import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Stack } from '@mui/material';
import { TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import PropTypes from 'prop-types';

import useForm from 'hooks/useForm';

import { closeDialogAction, showMessageAction } from 'store/global/actions';
import { decideLoanExceptionAction } from 'store/loanExceptions/actions';
import { LoanExceptionDecision, LoanExceptionOutcome } from 'store/loanExceptions/types';

import { noValidateInputProps } from '../Exceptions.constants';
import { ExceptionsTestIds } from '../Exceptions.testIds';

import { INITIAL_FORM_STATE } from './DecisionForm.constants';
import { formStackSx } from './DecisionForm.styles';

const DecisionForm = ({ exception, decision }) => {
  const dispatch = useDispatch();
  const { form, handleChange } = useForm(INITIAL_FORM_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isApprove = decision === LoanExceptionDecision.APPROVED;

  const onSubmit = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const outcome = isApprove
        ? LoanExceptionOutcome.APPROVED_AS_REQUESTED
        : LoanExceptionOutcome.DENIED;

      await dispatch(
        decideLoanExceptionAction({
          id: exception.id,
          payload: { id: exception.id, outcome, decisionNote: form.decisionNote.trim() || null },
        }),
      );

      dispatch(closeDialogAction());
    } catch (error) {
      dispatch(
        showMessageAction({
          variant: 'error',
          message: error?.response?.data?.message || 'Could not record decision',
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, exception.id, form.decisionNote, isApprove, isSubmitting]);

  return (
    <Stack sx={formStackSx}>
      <TextFieldCDS
        multiline
        dataTestId={ExceptionsTestIds.DECISION_NOTES_INPUT}
        inputProps={noValidateInputProps}
        label="Decision notes"
        minRows={3}
        name="decisionNote"
        value={form.decisionNote}
        onChange={handleChange}
      />

      <Button
        color={isApprove ? 'primary' : 'error'}
        data-testid={isApprove ? ExceptionsTestIds.APPROVE_BUTTON : ExceptionsTestIds.DENY_BUTTON}
        disabled={isSubmitting}
        variant="contained"
        onClick={onSubmit}
      >
        {isApprove ? 'Approve' : 'Deny'}
      </Button>
    </Stack>
  );
};

DecisionForm.propTypes = {
  exception: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  decision: PropTypes.oneOf(Object.values(LoanExceptionDecision)).isRequired,
};

export default DecisionForm;
